import { token } from '@atlaskit/tokens';
import { useFlaggingCallback } from '@databases/flags';
import ValidationStatus, {
  ValidationStatusContainer,
} from '@databases/form/ValidationStatus';
import { messages } from '@databases/intl/messages';
import { defaultStoreUtils } from '@databases/redux';
import { Adjacents } from '@databases/redux/cellSelections/selectors';
import { addChangeToDraft } from '@databases/redux/changesets/actions';
import { selectDatabaseDraftByContentId } from '@databases/redux/changesets/selectors';
import { doClearValues } from '@databases/redux/contextual-menu/actions';
import { useSkeletonEnabled } from '@databases/skeleton';
import { DatabaseValue, DatabaseFieldType } from '@databases/types/database';
import { DatabaseChangeType } from '@databases/utils/database/changesets';
import { getValidationError } from '@databases/utils/database/field-types';
import {
  getDatabaseEntryRequired,
  getDatabaseFieldRequired,
  getDatabaseValue,
} from '@databases/utils/database/utils';
import React, { FunctionComponent, useState } from 'react';
import { useIntl } from 'react-intl-next';
import styled from 'styled-components';

import { PresenceInfo, usePresence } from '../../presence';
import { InlineEditContainer, InlineEditStateless } from '../InlineEdit';
import InlineEditWrapper from '../InlineEditWrapper';
import { useScope } from '../Scope';
import { EditMode, FIELD_TYPES } from '../field-types/fieldTypes';

import DatabaseValueEdit from './DatabaseValueEdit';
import DatabaseValueView from './DatabaseValueView';
import { DatabaseValueViewContainer } from './utils';

export const StyledInlineEditWrapper = styled(InlineEditWrapper)<{
  isSelected?: boolean;
  isFirstInRow?: boolean;
  selectedAdjacentCells?: Adjacents;
  presenceInfo?: PresenceInfo;
}>`
  height: 100%;
  > ${ValidationStatusContainer},
    > ${ValidationStatusContainer}
    > ${InlineEditContainer} {
    flex-grow: 1;
    *:not(span):not(div) {
      background-color: transparent;
    }
    *:not(a):not(span) {
      // Among other things, this hides the border of input fields because we
      // want to use the border of the BorderContainer. We want to use the smart
      // card-provided border for smart cards, so we exclude <a> and <span>. In
      // NCTD-2440, we want to look into improving this styling.
      border-color: transparent;
    }
  }

  &:hover {
    > ${ValidationStatusContainer} > ${InlineEditContainer} {
      background: ${({ isSelected }) =>
        isSelected ? 'initial' : token('elevation.surface.hovered')};
    }
  }

  > ${DatabaseValueViewContainer},
    > div
    > ${DatabaseValueViewContainer},
    > ${ValidationStatusContainer}
    > ${InlineEditContainer}
    > div
    > ${DatabaseValueViewContainer} {
    display: inline-block;
    padding: ${({ isFirstInRow }) => (isFirstInRow ? '10px 8px 10px 20px' : '10px 8px')};
    max-width: 100%;
    box-sizing: border-box;
  }

  min-height: 41px !important;

  && textarea {
    padding: ${({ isFirstInRow }) => (isFirstInRow ? '8px 6px 8px 18px' : '8px 6px')};
  }

  && input {
    padding: ${({ isFirstInRow }) => isFirstInRow && '8px 6px 8px 18px'};
  }

  ${({ presenceInfo }) =>
    presenceInfo &&
    `z-index: 2;

  &::before {
    content: attr(data-presence-name);
    max-width: 65%;
    color: white;
    position: absolute;
    bottom: 100%;
    right: 0;
    height: fit-content;
    background: ${presenceInfo.color};
    pointer-events: none;
    text-align: right;
    padding: ${token('space.025')} ${token('space.050')} ${token('space.025')} ${token(
      'space.050'
    )};
    border-radius: 4px 4px 0 0;
  }

  `}
`;

export interface DatabaseValueInlineEditStatelessProps {
  contentId: string;
  fieldId: string;
  entryId: string;
  isEditing: boolean;
  onIsEditingChange: (isEditing: boolean) => void;
  isReadonly?: boolean;
  isContentEditable?: boolean;
  isSelected?: boolean;
  markAsDeleted?: boolean;
  onIsSelectedChange?: (options?: { isMultiSelectOn?: boolean }) => void;
  isFirstInRow?: boolean;
  selectedAdjacentCells?: Adjacents;
  enableSelection?: boolean;
}

/**
 * Renders an inline edit field to edit a database value. Automatically picks the right view/edit components based
 * on the field type of the value. When saving the changes, a database update is automatically drafted.
 */
export const DatabaseValueInlineEditStateless: FunctionComponent<
  DatabaseValueInlineEditStatelessProps
> = ({
  contentId,
  fieldId,
  entryId,
  isEditing,
  onIsEditingChange,
  isReadonly,
  isContentEditable,
  isSelected,
  markAsDeleted,
  onIsSelectedChange,
  isFirstInRow,
  selectedAdjacentCells,
  enableSelection,
}) => {
  const dispatch = defaultStoreUtils.useDispatch();
  const scope = useScope();
  const { formatMessage } = useIntl();
  const [value, validationError, editMode, field] = defaultStoreUtils.useEqualSelector(
    (state) => {
      const databaseDraft = selectDatabaseDraftByContentId(state, {
        contentId,
        scope,
        includeDeleted: true,
      });
      const entry = getDatabaseEntryRequired(databaseDraft, entryId);
      const field = getDatabaseFieldRequired(databaseDraft, fieldId);
      const value = getDatabaseValue(databaseDraft, field, entry);

      const editMode = FIELD_TYPES[field.type].editMode(
        { database: databaseDraft, field, entry, value },
        state
      );

      const validationError = getValidationError(
        { database: databaseDraft, field, entry, value },
        state
      );

      return [value, validationError, editMode, field] as const;
    }
  );

  const handleReset = useFlaggingCallback(
    (value: DatabaseValue) => {
      dispatch(
        addChangeToDraft(contentId, scope, {
          type: DatabaseChangeType.SET_VALUE,
          fieldId,
          entryId,
          value,
          change: null, // ignore reset in collab mode, since we don't know what delta to pass?
        })
      );
    },
    [contentId, dispatch, entryId, fieldId, scope]
  );

  const handleClearValues = useFlaggingCallback(() => {
    dispatch(doClearValues(contentId));
  }, [contentId, dispatch]);

  const fieldSettingsRequired =
    (field.type === DatabaseFieldType.JIRA_ISSUE &&
      !field.settings.jiraServerId &&
      !field.settings.cloudId) ||
    (field.type === DatabaseFieldType.ENTRY_LINK &&
      !field.settings.databaseId &&
      !field.settings.contentId);

  const canEdit = !isReadonly && !fieldSettingsRequired;

  const isSkeletonEnabled = useSkeletonEnabled();
  const enableEditMode =
    !canEdit || editMode === EditMode.READ_ONLY || isSkeletonEnabled
      ? false
      : (isEditing && editMode === EditMode.INLINE_EDIT) || editMode === EditMode.EDIT;

  const presenceInfo = usePresence(contentId, entryId, fieldId, enableEditMode);

  return (
    <StyledInlineEditWrapper
      markAsDeleted={markAsDeleted}
      isInvalid={canEdit && !!validationError}
      hideBorderOnFocus
      isSelected={isSelected}
      isFirstInRow={isFirstInRow}
      selectedAdjacentCells={selectedAdjacentCells}
      presenceInfo={presenceInfo}
    >
      <ValidationStatus
        error={
          validationError
            ? formatMessage(messages[validationError as keyof typeof messages])
            : undefined
        }
      >
        {() => (
          <InlineEditStateless<DatabaseValue>
            isSelected={isSelected}
            readView={() => (
              <DatabaseValueView
                contentId={contentId}
                entryId={entryId}
                fieldId={fieldId}
                // Prevent focussing anything inside the value (such as a link) by using Tab, as we want the Tab key
                // to navigate between the inline-edit fields.
                noFocus
                isReadonly={isContentEditable === false}
              />
            )}
            editView={() => (
              <DatabaseValueEdit
                contentId={contentId}
                entryId={entryId}
                fieldId={fieldId}
                noValidation={editMode === EditMode.INLINE_EDIT}
                isSeamless={editMode === EditMode.EDIT}
                autoFocus={isEditing}
              />
            )}
            isEditing={enableEditMode}
            onIsEditingChange={onIsEditingChange}
            value={value}
            onReset={handleReset}
            handleClearValues={handleClearValues}
            onIsSelectedChange={onIsSelectedChange}
            enableSelection={enableSelection}
            field={field}
            entryId={entryId}
            contentId={contentId}
            tooltipContent={
              fieldSettingsRequired
                ? formatMessage(
                    messages['value-inline-edit.required-field-missing-tooltip']
                  )
                : null
            }
          />
        )}
      </ValidationStatus>
    </StyledInlineEditWrapper>
  );
};

export interface DatabaseValueInlineEditProps
  extends Omit<DatabaseValueInlineEditStatelessProps, 'isEditing' | 'onIsEditingChange'> {
  /** If true, the field will be in edit mode the first time it is rendered. */
  initialIsEditing?: boolean;
}

const DatabaseValueInlineEdit: FunctionComponent<DatabaseValueInlineEditProps> = ({
  initialIsEditing = false,
  ...props
}) => {
  const [isEditing, setIsEditing] = useState(initialIsEditing);
  return (
    <DatabaseValueInlineEditStateless
      isEditing={isEditing}
      onIsEditingChange={setIsEditing}
      {...props}
    />
  );
};

export default React.memo(DatabaseValueInlineEdit);
